import { inject, Injectable } from '@angular/core';
import { FilterableDataSource, LoadableDataSource } from '@vdms-hq/shared';
import { BehaviorSubject, combineLatest, debounceTime, map, Observable, of, switchMap, take, tap } from 'rxjs';
import { MandatoryField } from '@vdms-hq/api-contract';
import {
  FieldsFetcherService,
  FieldsMandatoryFieldsDataSourceProvider,
  FieldsSaverService,
  FieldsScopeKey,
} from '@vdms-hq/fields';
import { AuthService } from '@vdms-hq/auth';
import { MatDialog } from '@angular/material/dialog';
import { MandatoryFieldsInfoDialogComponent } from '../components/mandatory-fields-info-dialog/mandatory-fields-info-dialog.component';
import { MandatoryFieldsFetcherService } from './mandatory-fields-fetcher.service';

@Injectable({ providedIn: 'root' })
export class MandatoryFieldsDatasource
  implements Partial<LoadableDataSource>, Partial<FilterableDataSource>, FieldsMandatoryFieldsDataSourceProvider
{
  private fieldsFetcherService = inject(FieldsFetcherService);
  private mandatoryFieldsFetcher = inject(MandatoryFieldsFetcherService);
  private authService = inject(AuthService);
  private dialog = inject(MatDialog);
  private fieldsSaverService = inject(FieldsSaverService);

  isLoading$ = new BehaviorSubject(false);
  filterValue$ = new BehaviorSubject('');

  mandatoryFields$ = this.mandatoryFieldsFetcher.mandatoryFields$;

  allData$: Observable<MandatoryField[]> = combineLatest([this.mandatoryFields$, this.filterValue$]).pipe(
    tap(() => this.isLoading$.next(true)),
    debounceTime(250),
    map(([data, value]) => {
      if (value) {
        const needle = value.toLowerCase();
        return data.filter(
          (item) =>
            item.contentType.join(',').toLowerCase().includes(needle) ||
            item.assetType.join(',').toLowerCase().includes(needle) ||
            item.contentClass.join(',').toLowerCase().includes(needle) ||
            item.fields.join(',').toLowerCase().includes(needle),
        );
      }

      return data;
    }),
    tap(() => this.isLoading$.next(false)),
  );
  connection$ = this.allData$;

  total$ = this.mandatoryFields$.pipe(map((fields) => fields.length));

  popDialog(scope: FieldsScopeKey, options?: { manualTrigger?: boolean }) {
    return this.authService.userAttributes$.pipe(
      take(1),
      switchMap((userAttributes) => {
        if (userAttributes && userAttributes.launchpad?.mandatoryColumnsDialogFinished && !options?.manualTrigger) {
          return of(false);
        }
        return this.getMissingMandatoryFields(scope).pipe(
          switchMap((missingFields) => {
            if (missingFields.length === 0) {
              return of(false);
            }
            const dialogRef = this.dialog.open(MandatoryFieldsInfoDialogComponent, {
              disableClose: true,
              data: { manualTrigger: options?.manualTrigger },
            });
            return dialogRef
              .afterClosed()
              .pipe(
                switchMap((dontShow) =>
                  dontShow ? this.#markDialogAsFinished() : this.#updateUserConfig(scope, missingFields),
                ),
              );
          }),
        );
      }),
    );
  }

  #markDialogAsFinished() {
    return this.authService
      .updateAttributes({
        launchpad: {
          mandatoryColumnsDialogFinished: true,
        },
      })
      .pipe(map(() => false));
  }

  #updateUserConfig(scope: FieldsScopeKey, missingFields: string[]) {
    return this.fieldsFetcherService.getConfiguration$(scope).pipe(
      take(1),
      switchMap((config) => {
        const userFields = (config.table.user ?? config.table.client.enabled).map((f) => f.id);
        const mergedFields = [...userFields, ...missingFields];
        return this.fieldsSaverService.saveUserColumnsConfig(scope, mergedFields);
      }),
    );
  }

  getMissingMandatoryFields(scope: FieldsScopeKey, fieldsToCheck?: string[]) {
    return combineLatest([this.connection$, this.fieldsFetcherService.getConfiguration$(scope)]).pipe(
      take(1),
      map(([mandatoryFields, config]) => {
        const allMandatoryFields = mandatoryFields.flatMap((field) => field.fields);
        const uniqueMandatoryFields = [...new Set(allMandatoryFields)];
        const mandatoryFieldIds: string[] = [];

        config.table.client.available.forEach((field) => {
          if (
            (field.input?.objectPath && uniqueMandatoryFields.includes(field.input.objectPath)) ||
            field.id === 'metadataRequired'
          ) {
            mandatoryFieldIds.unshift(field.id);
          }
        });

        const userConfig = fieldsToCheck ?? (config.table.user ?? config.table.client.enabled).map((field) => field.id);
        return mandatoryFieldIds.filter((fieldId) => !userConfig.includes(fieldId));
      }),
    );
  }

  applyFilter(value: string) {
    this.filterValue$.next(value);
  }

  fetchMandatoryFields() {
    this.mandatoryFieldsFetcher.fetch();
  }

  resetMandatoryFields() {
    this.mandatoryFieldsFetcher.reset();
  }
}
